<template>
	<div id="container">
		<!-- Full page loading overlay -->
		<div v-if="isLoading" class="overlay">
			<div class="overlay-content">
				<img src="/src/assets/Gears_Loading.gif" :alt="$t('common.loading.imageAlt')" />
				<p style="margin-top: 20px; font-weight: bold">{{ loadingStatusMessage }}</p>
				<p>{{ store.loadingText }}</p>
			</div>
		</div>

		<!-- Close Button -->
		<button v-if="isEmbedded" class="close-button" @click="onCloseButtonClicked">
			<svg-icon style="color: black" type="mdi" :size="28" :path="mdiClose"></svg-icon>
		</button>

		<div v-if="isDevMode" class="dev-banner">
			<span>{{ $t("common.devMode") }}</span>
		</div>
		<div v-if="hasFatalError" class="error-banner">
			<span>{{ $t("common.errors.modelCannotBeShown") }}</span>
		</div>

		<cadView v-if="camModelLoaded" :fitCameraToObjectToggle="fitCameraToObjectToggle"></cadView>

		<!-- Left Drawer -->
		<div id="leftDrawer" class="drawer left">
			<button v-if="!isPanelOpen(Panel.Editor)" @click="togglePanel(Panel.Editor)">
				{{ $t("common.panels.editor") }}
			</button>
			<div v-show="isPanelOpen(Panel.Editor)" class="drawer-content">
				<EditorPanel
					@fit-camera-to-object="fitCameraToObjectToggle = !fitCameraToObjectToggle"
					@closePanel="togglePanel(Panel.Editor)"
				/>
			</div>
			<button
				v-if="!isPanelOpen(Panel.Mode) && store.isAnyEditModeSelected"
				@click="togglePanel(Panel.Mode)"
			>
				{{ $t("common.panels.mode") }}
			</button>
			<div v-show="isPanelOpen(Panel.Mode)" class="drawer-content grow">
				<ModePanel @closePanel="togglePanel(Panel.Mode)" />
			</div>
		</div>

		<!-- Right Drawer -->
		<div class="drawer right">
			<button v-if="!isPanelOpen(Panel.Article)" @click="togglePanel(Panel.Article)">
				{{ $t("common.panels.article") }}
			</button>
			<div v-show="isPanelOpen(Panel.Article)" class="drawer-content">
				<ArticlePanel
					@saveAndClose="saveAndClose"
					@closePanel="togglePanel(Panel.Article)"
				/>
			</div>
			<!-- Message -->
			<button
				v-if="store.hasOpenValidationMessages && !isPanelOpen(Panel.Messages)"
				@click="togglePanel(Panel.Messages)"
			>
				{{ $t("common.panels.messages") }}
			</button>
			<div
				v-show="store.hasOpenValidationMessages && isPanelOpen(Panel.Messages)"
				class="drawer-content grow"
			>
				<MessagePanel @closePanel="togglePanel(Panel.Messages)" />
			</div>
			<!-- Features -->
			<button v-if="!isPanelOpen(Panel.Features)" @click="togglePanel(Panel.Features)">
				{{ $t("common.panels.features") }}
			</button>
			<div v-show="isPanelOpen(Panel.Features)" class="drawer-content grow">
				<FeaturePanel @closePanel="togglePanel(Panel.Features)" />
			</div>
			<!-- Structure -->
			<button v-if="!isPanelOpen(Panel.Structure) && store.article.isAssembly" @click="togglePanel(Panel.Structure)">
				{{ $t("common.panels.structure") }}
			</button>
			<div v-show="isPanelOpen(Panel.Structure) && store.article.isAssembly" class="drawer-content grow">
				<StructurePanel @closePanel="togglePanel(Panel.Structure)" />
			</div>
		</div>

		<ErrorDialog :visible="store.errorDialog !== null" @close="store.errorDialog = null" />
		<UnsavedChangesDialog
			:visible="unsavedChangesDialog"
			@close="unsavedChangesDialog = false"
			@discardChanges="closeModel()"
		/>
	</div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import EditorPanel from "@/components/EditorPanel.vue";
import ArticlePanel from "@/components/ArticlePanel.vue";
import FeaturePanel from "@/components/FeaturePanel.vue";
import ModePanel from "@/components/ModePanel.vue";
import MessagePanel from "@/components/MessagePanel.vue";
import ErrorDialog from "@/components/ErrorDialog.vue";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog.vue";
import { BlexonErpApi } from "@/store/BlexonErpApi";
import { useMainStore } from "@/store/mainStore";
import { Panel, LoadingState, EditorMode } from "@/model/Enums";
import { setLocale, isSupportedLocale } from "@/plugins/i18n";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiClose } from "@mdi/js";
import * as signalR from "@microsoft/signalr";

const props = defineProps<{ articleId: string; token: string }>();

// Language isn't a route path param like articleId/token (see router.ts), it's passed as a
// query param instead, e.g. "...#/123/abc?language=fr-FR", so it's read via the route directly.
// setLocale() itself falls back to German for anything not in supportedLocales.
const route = useRoute();
const { t } = useI18n();

const store = useMainStore();
const hasFatalError = ref<boolean>(false)
const panels = ref<Panel[]>([]);
const camModelLoaded = ref<boolean>(false);
const fitCameraToObjectToggle = ref<boolean>(false);
const unsavedChangesDialog = ref<boolean>(false);

const init = async () => {
	// Load data
	// Load the cam model from the server
	const loadModelTask = store.cam.loadModel(Number(props.articleId))

	// Load article data
	const articleData = await BlexonErpApi.getArticleData(Number(props.articleId));

	// Setup an observer that watches the left drawer element.
	// If this changes height, the location of the 3D gizmo might need to be changed.
	const leftDrawer = document.getElementById("leftDrawer");
	const leftDrawerObserver = new ResizeObserver((elements) => {
		const gizmo = document.getElementById("gizmo");
		if (!gizmo) return; // If the gizmo is not found, the function is not needed.
		for (let ele of elements) {
			const height = ele.borderBoxSize[0].blockSize;
			const maxHeight = window.innerHeight - 128; // Gizmo is 128px high
			if (height >= maxHeight) {
				gizmo.style.left = "450px";
				store.renderGizmo = !store.renderGizmo;
			} else {
				gizmo.style.left = "0px";
				store.renderGizmo = !store.renderGizmo;
			}
		}
	});
	leftDrawerObserver.observe(leftDrawer!);

	// Load validation message translations
	BlexonErpApi.loadMessageTranslations(Number(props.articleId));

	// Old weldline definitions have to geometry in the scdoc not in the db.
	// We load the definition from the scdoc and copy it to the model
	if (articleData.weldlines && articleData.weldlines.length > 0) {
		if (articleData.weldlines.some((w) => w.version < 4)) {
			const weldlinesInModel = await BlexonErpApi.getWeldlines(articleData.article.id);
			for (const weldline of articleData.weldlines) {
				const weldlineInModel = weldlinesInModel.find(
					(w) => w.weldlineId === weldline.weldlineId
				);
				if (weldlineInModel !== undefined) {
					weldline.position = weldlineInModel.position;
					weldline.direction = weldlineInModel.direction;
					weldline.curve = weldlineInModel.curve;
					weldline.distance = weldlineInModel.distance;
					weldline.version = weldlineInModel.version;
					weldline.weldCorners = [];
					if (weldlineInModel.weldCorners != null) {
						for (const cornerInModel of weldlineInModel.weldCorners) {
							weldline.weldCorners.push(cornerInModel);
							for (const cornerposition of weldline.corners) {
								if (cornerInModel.position === cornerposition) {
									cornerInModel.weldCorner = true;
								}
							}
						}
					}
				}
			}
		}
	}

	store.article = articleData.article;
	store.features = articleData.features;
	store.validationMessages = articleData.validationMessages;
	store.weldlines.push(...articleData.weldlines);

	store.cam.addPressJoinBodiesToFeatures();

	// Set initial editor mode
	let initialMode = EditorMode.FeatureMode;
	if (store.article.isAssembly || store.weldlines.length > 0) {
		initialMode = EditorMode.WeldMode;
	}
	store.setEditorMode(initialMode);
	store.setAllWeldlinesVisibility(true);

	if (articleData.article.rawMaterialId > 0) {
		// Load Gewindeparameter
		const screwThreadData = await BlexonErpApi.getGewindeParameter(store.article.rawMaterialId);
		store.screwThreadParameters = screwThreadData;
		// Load Senkungsparameter
		const countersinkData = await BlexonErpApi.getSenkungParameter(store.article.rawMaterialId);
		store.countersinkParameters = countersinkData;
		// Load Zylinderkopfparameter
		const cylinderheadData = await BlexonErpApi.getCylinderheadBoltHoleParameter(
			store.article.rawMaterialId
		);
		store.cylinderheadBoltHoleParameters = cylinderheadData;
		// Load Durchzugsparameter
		const durchzugsData = await BlexonErpApi.getDurchzugParameter(store.article.rawMaterialId);
		store.extrudedThreadParameters = durchzugsData;
		// Load Validationsparameter
		const validationData = await BlexonErpApi.getValidationParameter(
			store.article.rawMaterialId,
			store.article.id
		);
		store.validationParameter = validationData;
		// Update relevancy of roll direction after getting validation data
		store.rollDirection.isRelevant = validationData.schliffrichtungRelevant;

		// Load press join
		const pressJoinParameter = await BlexonErpApi.getPressJoinParameter(
			store.article.rawMaterialId
		);
		store.pressJoinParameter = pressJoinParameter;
	}
	
	await loadModelTask;

	if (window.innerWidth > 1200) {
		togglePanel(Panel.Editor);
		togglePanel(Panel.Article);
		if (store.hasOpenValidationMessages) {
			togglePanel(Panel.Messages);
		}
		togglePanel(Panel.Features);
		togglePanel(Panel.Mode);
		togglePanel(Panel.Structure);
	}

};

const isLoading = computed((): boolean => {
	return store.loadingState != LoadingState.Done && store.loadingState != LoadingState.None;
});

const isEmbedded = computed((): boolean => {
	return window.location !== window.parent.location;
});

const isDevMode = computed((): boolean => {
	return import.meta.env.VITE_APP_MODE == "DEV";
});

const togglePanel = (tab: Panel) => {
	if (panels.value.includes(tab)) {
		// remove the box if already open
		panels.value = panels.value.filter((item) => item !== tab);
	} else {
		// Add the box to open it
		panels.value.push(tab);
	}
};

const isPanelOpen = (tab: Panel) => {
	return panels.value.includes(tab);
};

const onCloseButtonClicked = () => {
	if (store.isFileSaved) {
		closeModel();
	} else {
		unsavedChangesDialog.value = true;
	}
};

const closeModel = async () => {
	BlexonErpApi.close(Number.parseInt(props.articleId));
	window.parent.postMessage("article-" + props.articleId + "-closed", "*");
	unsavedChangesDialog.value = false;
};

const saveAndClose = async () => {
	console.log("Saving and closing the viewer...");
	try {
		store.setLoadingState(LoadingState.Saving);
		store.isFileSaved = true;
		setTimeout(async () => {
			window.parent.postMessage("article-" + store.article.id + "-saved", "*");
		}, 1000);
		await BlexonErpApi.saveScDoc(
			store.article.id,
			store.validationMessages,
			store.features,
			store.weldlines,
			store.repeatBendSimulation
		);
	} catch (error: any) {
		console.log("Error while saving: ", error);
		store.errorDialog = { title: t("common.errors.saveFailedTitle"), messages: [error.message] };
	} finally {
		store.setLoadingState(LoadingState.Done);
	}
};

/**
 * Returns the text which belongs to the current status. The text will
 * shown next to the loading animation.
 * @param status Status we want the text from
 * @returns Text of status
 */
const loadingStatusMessage = computed(() => {
	switch (store.loadingState) {
		case LoadingState.Loading:
			return t("common.loading.partIsLoading");
		case LoadingState.Saving:
			return t("common.loading.partIsSaving");
		case LoadingState.ChangingAnchorFace:
			return t("common.loading.anchorFaceIsChanging");
		case LoadingState.SettingFeature:
			return t("common.loading.featureIsSetting");
		case LoadingState.MarkFeatures:
			return t("common.loading.whileMarkingFeaturesMessage");
		default:
			return "";
	}
});

const tokenSetup = () => {
	store.setLoadingState(LoadingState.Loading);
	store.token = props.token;
};

const languageSetup = () => {
	const language = route.query.language;
	store.language = typeof language === "string" && isSupportedLocale(language) ? language : "de-CH";
	setLocale(store.language);
};

const setupHeartbeat = () => {
	setInterval(() => {
		BlexonErpApi.live(store.token, props.articleId);
	}, 10000);
};

/**
 * beforeunload is called when the browser or the tab is closed.
 * The Browser doesn't want to wait for the promise to resolve, so this
 * process is tricky. Different browsers could handle it differently.
 * The result doesn't need to be awaited, it just needs to fire.
 */
const setupUnload = () => {
	window.addEventListener("beforeunload", async () => {
		await BlexonErpApi.close(Number.parseInt(props.articleId)).catch(() => {});
	});
};

const setupSignalR = () => {
	const url = import.meta.env.VITE_APP_EVENT_URL;
	const connection = new signalR.HubConnectionBuilder()
		.withUrl(`${url}camsignalhub`, { accessTokenFactory: () => store.token })
		.configureLogging(signalR.LogLevel.Information)
		.build();
	connection.start();
	connection.on("CamEvent", (payload: any) => {
		const payloadAsObject = JSON.parse(payload.jsonObject);
		store.loadingText = payloadAsObject.text;
	});
};

onBeforeMount(() => {
	tokenSetup();
	languageSetup();
	setupHeartbeat();
	setupUnload();
	setupSignalR();
});

onMounted(async () => {
	try {
		await init();
		camModelLoaded.value = true;
	} catch (exception) {
		console.log(exception)
		hasFatalError.value = true
	} finally {
		store.setLoadingState(LoadingState.Done);
	}
});
</script>

<style>
/* Drawer containers */
.drawer {
	position: fixed;
	display: flex;
	flex-direction: column;
	top: 0;
	max-height: 100%;
	min-width: 200px;
	max-width: 450px;
	padding-top: 3px;
	padding-bottom: 3px;
	gap: 3px;
	z-index: 10;
	overflow: hidden;
}

/* Left Drawer */
.drawer.left {
	padding-left: 3px;
	left: 0;
}

/* Right Drawer */
.drawer.right {
	padding-right: 3px;
	right: 0;
}

/* Drawer content panels */
.drawer-content {
	display: flex;
	flex-direction: column;
	background: whitesmoke;
	padding: 10px;
	border: 1px solid var(--default-border-color);
	border-radius: 4px;
}
.drawer-content.grow {
	flex-grow: 1;
	overflow: auto;
}

/* Drawer Buttons */
.drawer > button {
	padding: 0px;
	writing-mode: sideways-lr;
	width: 25px;
	height: 100px;
	font-size: 14px;
}
.drawer.left > button {
	border-radius: 0px 12px 12px 0px;
}
.drawer.right > button {
	border-radius: 12px 0px 0px 12px;
	/* Pin the registry buttons for the right drawer to the right */
	align-self: flex-end;
}

.close-button {
	position: fixed;
	top: 5px;
	right: 35px;
	background-color: var(--blexon-color);
	box-shadow: 5px 5px 10px -2px rgba(69, 69, 69, 0.9);
	width: 42px;
	height: 42px;
	border-radius: 50%;
	padding: 0px;
	margin: 0px;
	z-index: 11;
}

.dev-banner {
	position: fixed;
	top: 5px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 11;

	display: flex;
	justify-content: center;
	align-items: center;

	padding: 6px 14px;
	background-color: red;
	color: white;
	border-radius: 12px;
	white-space: nowrap;
}

.error-banner {
	position: fixed;
	top: 25px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 11;

	display: flex;
	justify-content: center;
	align-items: center;

	padding: 6px 14px;
	background-color: red;
	color: white;
	border-radius: 12px;
	white-space: nowrap;
}

/* Justify the loading animation. Absolute joke that this is not the default
    They even provide a slot for text but don't center it below the animation. */
.vl-icon {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
}

.overlay {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	background-color: rgba(255, 255, 255, 0.5); /* Semi-transparent white */
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 9999;
}

.overlay-content {
	text-align: center;
}

.overlay-content img {
	width: 200px;
	height: 200px;
}

.overlay-content p {
	font-size: 15px;
	color: #333;
}
</style>
